import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, BarChart3, Bell, CreditCard, Sparkles, LogOut, Mail, ChevronRight, BadgeCheck, ClipboardCheck, Download, Route, User } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { DimensionBadge, LevelBadge, DIM_STYLE } from "@/components/Badges";
import { ShareArchetype } from "@/components/ShareArchetype";
import { ContactDialog } from "@/components/ContactDialog";
import { InviteCard } from "@/components/InviteCard";
import { PushHealth } from "@/components/PushHealth";

export default function Profilo() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [arch, setArch] = useState(null);
  const pid = user?.profile_id || user?.assessment?.profile_id;

  useEffect(() => {
    if (pid) api.get(`/profiles/${pid}`).then(({ data }) => setArch(data)).catch(() => {});
  }, [pid]);

  const doLogout = async () => {
    await logout();
    toast.success("A presto!");
    navigate("/", { replace: true });
  };

  const openPortal = async () => {
    const inIframe = window.self !== window.top;
    const win = inIframe ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      if (win) win.location.href = data.portal_url;
      else window.location.href = data.portal_url;
    } catch (e) {
      if (win) win.close();
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sub = user?.subscription_active;
  const planLabel = user?.plan === "annual" ? "Annuale · €49,99/anno" : user?.plan === "monthly" ? "Mensile · €7,99/mese" : "Nessun piano attivo";
  const accent = arch ? DIM_STYLE[arch.dimension]?.c : "#D4AF37";

  const rows = [
    sub && { icon: Route, label: "Cambia percorso", desc: "Scegli tra le 6 aree", to: "/scegli", id: "cambia" },
    sub && { icon: BookOpen, label: "Diario", desc: "Le tue riflessioni", to: "/diario", id: "diario" },
    sub && { icon: BarChart3, label: "Statistiche", desc: "Progressi e traguardi", to: "/statistiche", id: "statistiche" },
    { icon: Bell, label: "Notifiche e promemoria", desc: "Push, email e orari", to: "/impostazioni", id: "impostazioni" },
    { icon: Download, label: "Come iniziare · Installa l'app", desc: "Guida ai primi passi", to: "/installa", id: "installa" },
    { icon: ClipboardCheck, label: "Rifai il test", desc: "18 domande, ricalcola l'archetipo", to: "/test", id: "rifai-test" },
  ].filter(Boolean);

  return (
    <AppShell title="Profilo" greeting={user?.name}>
      <div className="px-5 pt-6">
        {arch ? (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-2xl border p-5"
            style={{ borderColor: `${accent}44`, background: `linear-gradient(135deg, ${accent}14, #0D0D0F)` }}
          >
            <div className="flex items-center gap-2">
              <DimensionBadge dimension={arch.dimension} title={arch.dimension_title} />
              <LevelBadge level={arch.level} title={arch.level_title} />
            </div>
            <p className="eyebrow mt-4" style={{ color: accent }}>Il tuo archetipo</p>
            <h1 className="font-serif-el mt-1 text-3xl font-semibold text-[#FAF9F6]">{arch.archetype.title}</h1>
            <p className="mt-1 text-lg text-[#D4D4D8] font-semibold">{arch.archetype.subtitle}</p>
            <div className="mt-4">
              <ShareArchetype arch={arch.archetype} dimension={arch.dimension} level={arch.level} scores={user?.assessment?.scores} />
            </div>
          </motion.div>
        ) : (
          <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-6 text-center">
            <p className="text-base text-[#B4B4BC]">Non hai ancora un archetipo.</p>
            <button onClick={() => navigate("/test")} className="btn-gold mt-4 rounded-full px-5 py-2.5 text-sm uppercase tracking-wide" data-testid="profilo-start-test">
              Fai il test
            </button>
          </div>
        )}

        <PushHealth enabled={!!user?.notif_prefs?.push_enabled} className="mt-5" />

        <div className="mt-5 overflow-hidden rounded-2xl border border-[#27272A] bg-[#121215]">
          {rows.map((r, i) => (
            <button
              key={r.id}
              onClick={() => navigate(r.to)}
              data-testid={`profilo-row-${r.id}`}
              className={`flex w-full items-center gap-3 p-4 text-left transition active:bg-[#18181C] ${i > 0 ?"border-t border-[#27272A]" : ""}`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><r.icon size={17} /></span>
              <div className="flex-1">
                <p className="font-medium text-[#F4F4F5]">{r.label}</p>
                <p className="text-sm text-[#8A8E96]">{r.desc}</p>
              </div>
              <ChevronRight size={17} className="text-[#8A8E96]" />
            </button>
          ))}
        </div>

        <InviteCard />

        <div className="mt-5 rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <p className="eyebrow">Abbonamento</p>
          <div className="mt-3">
            {sub ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-1 text-base font-semibold text-[#10B981]">
                <BadgeCheck size={15} /> Attivo · prova 7 giorni
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-1 text-base font-semibold text-[#F87171]">
                Non attivo
              </span>
            )}
          </div>
          <p className="mt-2 text-base text-[#B4B4BC]">{planLabel}</p>
          {sub ? (
            <button onClick={openPortal} data-testid="manage-subscription-button" className="btn-outline-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-base">
              <CreditCard size={16} /> Gestisci abbonamento
            </button>
          ) : (
            <button onClick={() => navigate("/paywall")} data-testid="account-subscribe-button" className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-base uppercase tracking-wide">
              <Sparkles size={16} /> Attiva il percorso
            </button>
          )}
        </div>

        <button onClick={() => navigate("/account")} data-testid="profile-account-button" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-[#27272A] bg-[#121215] px-4 py-3.5 font-semibold text-[#E4E4E7] active:scale-[0.99]">
          <User size={16} className="text-[#D4AF37]" /> I miei dati: nome, email, telefono, password
        </button>

        <ContactDialog
          trigger={
            <button data-testid="profile-contact-button" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-[#27272A] bg-[#121215] px-4 py-3.5 font-semibold text-[#E4E4E7] active:scale-[0.99]">
              <Mail size={16} className="text-[#D4AF37]" /> Scrivici: problemi, domande, suggerimenti
            </button>
          }
        />

        {user?.role === "admin" && (
          <button onClick={() => navigate("/admin/coach")} data-testid="admin-coach-link" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#121215] px-4 py-3.5 font-semibold text-[#D4AF37] active:scale-[0.99]">
            Regole Coach Mino
          </button>
        )}
        {user?.role === "admin" && (
          <button onClick={() => navigate("/admin/campagne")} data-testid="admin-campaigns-link" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#121215] px-4 py-3.5 font-semibold text-[#D4AF37] active:scale-[0.99]">
            Campagne e Meta Pixel
          </button>
        )}

        <div className="mt-5 flex items-center gap-2 px-1 text-base text-[#8A8E96]">
          <Mail size={14} /> {user?.email}
        </div>

        <button onClick={doLogout} data-testid="logout-button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#27272A] bg-[#121215] px-4 py-3.5 font-semibold text-[#F87171] active:scale-[0.99]">
          <LogOut size={17} /> Esci
        </button>
      </div>
    </AppShell>
  );
}
